import { useState } from "react";
import { toast } from "sonner";
import { Image as ImageIcon, MessageCircle, Instagram, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { imageUrl } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const loadImg = (src) => new Promise((res) => { const i = new Image(); i.crossOrigin = "anonymous"; i.onload = () => res(i); i.onerror = () => res(null); i.src = src; });

function wrap(ctx, text, max) {
  const words = String(text).split(" "); const lines = []; let line = "";
  words.forEach((w) => { const t = line ? `${line} ${w}` : w; if (ctx.measureText(t).width > max && line) { lines.push(line); line = w; } else line = t; });
  if (line) lines.push(line);
  return lines.slice(0, 2);
}

async function renderAdImage({ cover, title, price, place, url, rtl }) {
  const W = 1080, H = 1350, c = document.createElement("canvas");
  c.width = W; c.height = H;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#FAF8F3"; ctx.fillRect(0, 0, W, H);
  const img = cover && await loadImg(imageUrl(cover));
  if (img) {
    const s = Math.max(W / img.width, 900 / img.height);
    ctx.save(); ctx.beginPath(); ctx.rect(0, 0, W, 900); ctx.clip();
    ctx.drawImage(img, (W - img.width * s) / 2, (900 - img.height * s) / 2, img.width * s, img.height * s); ctx.restore();
  }
  ctx.direction = rtl ? "rtl" : "ltr"; ctx.textAlign = rtl ? "right" : "left";
  const x = rtl ? W - 60 : 60;
  ctx.fillStyle = "#0F172A"; ctx.font = "bold 58px Cairo, Tajawal, sans-serif";
  const lines = wrap(ctx, title, W - 120);
  lines.forEach((l, i) => ctx.fillText(l, x, 990 + i * 70));
  const py = 990 + lines.length * 70 + 40;
  ctx.fillStyle = "#1A5C3A"; ctx.font = "800 64px Cairo, Tajawal, sans-serif"; ctx.fillText(price, x, py);
  ctx.fillStyle = "#64748B"; ctx.font = "36px Cairo, Tajawal, sans-serif"; ctx.fillText(place, x, py + 62);
  ctx.fillStyle = "#0F4D32"; ctx.fillRect(0, H - 80, W, 80);
  ctx.fillStyle = "#fff"; ctx.textAlign = "center"; ctx.direction = "ltr"; const foot = `Bazaroo · bazaroouae.com${new URL(url).pathname}`;
  let fs = 30;
  do { ctx.font = `bold ${fs}px sans-serif`; fs -= 2; } while (ctx.measureText(foot).width > W - 60 && fs > 14);
  ctx.fillText(foot, W / 2, H - 28);
  return new Promise((res) => c.toBlob(res, "image/png"));
}

export function ShareAsImage({ listing, title, price, place }) {
  const { lang, dir } = useLang();
  const T = (ar, en) => (lang === "en" ? en : ar);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const url = window.location.href;

  const share = async (app) => {
    setBusy(app);
    try {
      const blob = await renderAdImage({ cover: listing.images?.[0], title, price, place, url, rtl: dir === "rtl" });
      const file = new File([blob], `bazaroo-${listing.id}.png`, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] }) && /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)) {
        await navigator.share({ files: [file], text: `${title}\n${url}` });
      } else {
        const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = file.name; a.click();
        if (app === "whatsapp") window.open(`https://wa.me/?text=${encodeURIComponent(`${title} - ${price}\n${url}`)}`, "_blank");
        else toast.success(T("تم تنزيل الصورة، ارفعها على إنستغرام", "Image downloaded, upload it to Instagram"));
      }
      setOpen(false);
    } catch (e) { if (e?.name !== "AbortError") toast.error(T("تعذّرت المشاركة", "Couldn't share")); }
    finally { setBusy(null); }
  };

  const Opt = ({ app, icon: Icon, label, cls }) => (
    <button onClick={() => share(app)} disabled={!!busy} className={`flex-1 h-24 rounded-2xl flex flex-col items-center justify-center gap-2 font-semibold text-white ${cls}`} data-testid={`share-image-${app}`}>
      {busy === app ? <Loader2 className="w-7 h-7 animate-spin" /> : <Icon className="w-7 h-7" />}{label}
    </button>
  );
  return (
    <>
      <button onClick={() => setOpen(true)} className="w-full h-12 rounded-2xl border border-[#EFEBE0] bg-white font-semibold flex items-center justify-center gap-2 hover:bg-[#1A5C3A]/5 transition-colors" data-testid="detail-share-image">
        <ImageIcon className="w-5 h-5 text-[#1A5C3A]" />{T("مشاركة كصورة", "Share as image")}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir={dir} className="max-w-sm" data-testid="share-image-dialog">
          <DialogHeader><DialogTitle>{T("مشاركة كصورة", "Share as image")}</DialogTitle></DialogHeader>
          <div className="flex gap-3">
            <Opt app="whatsapp" icon={MessageCircle} label={T("واتساب", "WhatsApp")} cls="bg-[#25D366]" />
            <Opt app="instagram" icon={Instagram} label={T("إنستغرام", "Instagram")} cls="bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF]" />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
